import { useMemo } from 'react';
import { add, format } from 'date-fns';
import c from 'classnames';

import useConsults from 'services/general/consultsList';
import { CONSULT_ACTIVE_STATUS } from 'assets';
import { sortRecentToOldestConsult } from 'views/Consults/Consults';
import { Card } from 'components';
import { CONSULT_TYPE_MAP } from '@repo/app-constants';
import { Icon, Typography } from '@repo/ui';
import styles from './ConsultReminder.module.scss';
import useFeature from 'services/features/useFeature';
import { THMoment } from '@repo/utils';
import { getTimezoneAbbreviation } from 'views/Consults/ConsultCard/ConsultCard';

export const ConsultReminder = ({ className = '' }: { className?: string }) => {
  const { data: consultsList } = useConsults();
  const { isFeatureEnabled: showGroupCalls } = useFeature(
    'consult_group_calls',
  );

  const nextConsult = useMemo(() => {
    const upcomingConsults = (consultsList ?? [])
      .filter(
        consult =>
          CONSULT_ACTIVE_STATUS.includes(consult.status) &&
          consult.scheduled_dt >
            THMoment.getZeroDate(Date.now()).getTime() / 1000,
      )
      .sort(sortRecentToOldestConsult);
    return upcomingConsults.pop();
  }, [consultsList]);

  const agentName = nextConsult?.consult_agent?.first_name
    ? `${nextConsult.consult_agent.first_name} ${nextConsult.consult_agent.last_name}`
    : 'our team';

  if (!consultsList || !nextConsult) return <></>;

  return (
    <Card
      variant='wrapper'
      clickable={{
        text: 'Upcoming: Coaching session',
        href: '/consults',
        variety: 'Card',
        isExternal: false,
      }}
      className={c(styles.card, 'blue-border-left', className)}
    >
      <div className='flex flex-column gap-2'>
        <div className='flex align-items-center gap-1 blue-icon'>
          <Icon name='calendar' size='m' />
          <Typography variant='heading-m'>
            {showGroupCalls
              ? `Upcoming: ${(nextConsult?.type && CONSULT_TYPE_MAP[nextConsult.type]) || 'Coaching'} session`
              : `Upcoming: Coaching session`}
          </Typography>
        </div>
        <div className='flex align-item-center justify-between gap-1'>
          <Typography variant='label'>{`With ${agentName}`}</Typography>

          <div className='flex align-item-center gap-1'>
            <Typography variant='label'>
              {format(nextConsult?.scheduled_dt * 1000, 'EEE MMM d')}
            </Typography>
            <Typography variant='label'>
              {format(nextConsult?.scheduled_dt * 1000, 'hh:mm')}
            </Typography>
            <Typography variant='label'>
              {`- ${format(
                add(nextConsult?.scheduled_dt * 1000, { minutes: 30 }),
                'hh:mm a',
              )}`}
            </Typography>
            <Typography variant='label'>
              {getTimezoneAbbreviation(
                new Date(nextConsult?.scheduled_dt * 1000),
              )}
            </Typography>
          </div>
        </div>
      </div>
    </Card>
  );
};
